import type { Encoding } from "@/types/api";
import { sourceEncodingRule } from "@/utils/providerCapabilities";
import type { StagedSchema } from "./stagingModel";

/** Whether a staged schema is attached to the source row — what an Avro
 *  source needs before it can decode anything. */
export function sourceHasSchema(
  schemas: readonly StagedSchema[],
  sourceId: string,
): boolean {
  return schemas.some((schema) =>
    schema.attachments.some((a) => a.role === "source" && a.id === sourceId),
  );
}

// The "Source delivers" footer control's state (TTTL-268), kept pure so the
// component stays a thin renderer. What the control may do follows the
// engine's per-provider rule (sourceEncodingRule).

export const SOURCE_DELIVERS_OPTIONS: readonly Encoding[] = [
  "json",
  "avro",
  "bson",
];

export const ENCODING_LABELS: Record<Encoding, string> = {
  json: "JSON",
  avro: "Avro",
  bson: "BSON",
};

/** Avro frames carry no schema of their own: without a schema_id on the
 *  source the engine cannot decode a single record. */
export const AVRO_NEEDS_SCHEMA =
  "Avro requires a schema. Attach one to the source or the engine cannot decode it.";

export type SourceDeliversState =
  | { kind: "hidden" }
  | { kind: "editable"; value: Encoding; warning: string | null }
  | { kind: "fixed"; value: Encoding; reason: string };

export function sourceDeliversState(
  provider: string | undefined,
  stored: Encoding | undefined,
  schemaAttached: boolean,
  providerLabel: string,
): SourceDeliversState {
  const rule = sourceEncodingRule(provider);
  switch (rule.kind) {
    case "none":
      return { kind: "hidden" };
    case "fixed":
      return {
        kind: "fixed",
        value: rule.value,
        reason: `A ${providerLabel} source always delivers ${ENCODING_LABELS[rule.value]}.`,
      };
    case "required":
    case "optional": {
      // Nothing stored shows what the generated code will say: the Stages
      // default for Kafka/Pub/Sub, the engine's own default for HTTP.
      const value =
        stored ?? (rule.kind === "optional" ? rule.fallback : "json");
      const warning =
        value === "avro" && !schemaAttached ? AVRO_NEEDS_SCHEMA : null;
      return { kind: "editable", value, warning };
    }
  }
}

// The trigger's accessible name. Its visible text is the bare encoding (the
// mock's "Avro" button); "Source delivers", not "Encoding", because the
// control sits under the sample document, which is always JSON, and a bare
// "Encoding" could read as how the sample is parsed.
export function triggerLabel(value: Encoding): string {
  return `Source delivers: ${ENCODING_LABELS[value]}`;
}
